<script setup lang="ts">
// Menu profilu (SPEC.md 2.2): jméno, aktivní firma, přepnutí firmy, další firma, odhlášení.
// Na mobilu je uvnitř menu „Více“ (vždy rozbalené), na desktopu dole v postranní navigaci (sbalitelné).
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Building2, Check, ChevronDown, LogOut, Plus, UserRound } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'

withDefaults(defineProps<{ collapsible?: boolean }>(), { collapsible: false })
const emit = defineEmits<{ navigate: [] }>()

const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()

const open = ref(false)
const error = ref('')

async function pick(orgId: string) {
  error.value = ''
  if (orgId === auth.me?.org?.id) return
  const res = await auth.switchOrg(orgId)
  if (res.ok) {
    emit('navigate')
    await router.push('/')
  } else error.value = res.errorKey
}

async function logout() {
  emit('navigate')
  await auth.signOut()
}
</script>

<template>
  <section v-if="auth.me" class="flex flex-col gap-1" :aria-label="t('profile.menu')">
    <component
      :is="collapsible ? 'button' : 'div'"
      :type="collapsible ? 'button' : undefined"
      class="flex min-h-tap items-center gap-3 rounded-md px-3 text-left text-text"
      :class="collapsible ? 'hover:bg-surface-2' : ''"
      :aria-expanded="collapsible ? open : undefined"
      data-testid="profile-toggle"
      @click="collapsible && (open = !open)"
    >
      <UserRound :size="24" aria-hidden="true" class="shrink-0" />
      <span class="min-w-0 flex-1">
        <span class="block truncate font-semibold" data-testid="profile-name">{{
          auth.me.user.name
        }}</span>
        <span class="block truncate text-sm text-text-muted" data-testid="profile-org">{{
          auth.me.org?.name ?? t('profile.noOrg')
        }}</span>
      </span>
      <ChevronDown
        v-if="collapsible"
        :size="20"
        aria-hidden="true"
        class="shrink-0 transition-transform"
        :class="open ? 'rotate-180' : ''"
      />
    </component>

    <div v-if="!collapsible || open" class="flex flex-col gap-1">
      <template v-if="auth.me.orgs.length > 0">
        <p class="m-0 mt-1 px-3 text-xs font-semibold uppercase text-text-subtle">
          {{ t('profile.switchOrg') }}
        </p>
        <button
          v-for="o in auth.me.orgs"
          :key="o.id"
          type="button"
          class="flex min-h-tap items-center gap-3 rounded-md px-3 text-left text-text hover:bg-surface-2"
          :aria-current="o.id === auth.me.org?.id ? 'true' : undefined"
          @click="pick(o.id)"
        >
          <Building2 :size="20" aria-hidden="true" class="shrink-0 text-text-muted" />
          <span class="min-w-0 flex-1 truncate">{{ o.name }}</span>
          <Check
            v-if="o.id === auth.me.org?.id"
            :size="20"
            aria-hidden="true"
            class="shrink-0 text-primary-text"
          />
        </button>
      </template>
      <p v-if="error" class="m-0 px-3 text-sm text-danger" role="alert">{{ t(error) }}</p>

      <RouterLink
        to="/zalozeni-firmy"
        class="flex min-h-tap items-center gap-3 rounded-md px-3 text-text no-underline hover:bg-surface-2"
        @click="emit('navigate')"
      >
        <Plus :size="20" aria-hidden="true" class="shrink-0 text-text-muted" />
        <span>{{ t('profile.newOrg') }}</span>
      </RouterLink>
      <button
        type="button"
        class="flex min-h-tap items-center gap-3 rounded-md px-3 text-left text-text hover:bg-surface-2"
        @click="logout"
      >
        <LogOut :size="20" aria-hidden="true" class="shrink-0 text-text-muted" />
        <span>{{ t('profile.logout') }}</span>
      </button>
    </div>
  </section>
</template>
